// src/pages/KioskPage.js — bilingual EN/AR
// FIXES:
//   1. Order created via /orders (new controller with OrderNumberService)
//   2. Fallback order uses orderNumber from API, not ORD-random
//   3. Cash payment calls mark-paid to update DB
//   4. PaymentModal receives sourceKiosk so cash works correctly

import React, { useState, useEffect } from 'react';
import { getMenu }          from '../services/api';
import { useCart }          from '../context/CartContext';
import { useLanguage }      from '../context/LanguageContext';
import useKioskMode         from '../hooks/useKioskMode';
import usePWA               from '../hooks/usePWA';
import IdleScreen           from '../components/IdleScreen';
import PromoCode            from '../components/PromoCode';
import PaymentModal         from '../components/PaymentModal';
import ModifierSelector     from '../components/ModifierSelector';
import BundleConfigurator   from '../components/BundleConfigurator';
import LangToggle           from '../components/LangToggle';
import strings              from '../i18n/translations';
import api                  from '../services/api';
import { applyTheme }       from '../utils/applyTheme';
import { calculateServiceFee } from '../utils/serviceFeeCalculator';
import { applyDietary, categoriesOf } from '../utils/dietary';
import DietarySheet         from '../components/DietarySheet';
import { DietaryPill, DietaryHiddenNote, UnverifiedTag } from '../components/DietaryFilterUI';

const IDLE_AFTER = 60 * 1000;

const MOCK_MENU = [
  { id:'1', name:'Shawarma Wrap',    nameAr:'شاورما',           price:22, category:'Wraps',   categoryAr:'لفائف', emoji:'🌯', isActive:true },
  { id:'2', name:'Cheese Burger',    nameAr:'برجر بالجبن',      price:28, category:'Burgers', categoryAr:'برجر',  emoji:'🍔', isActive:true },
  { id:'3', name:'Margherita Pizza', nameAr:'بيتزا مرغريتا',    price:29, category:'Pizza',   categoryAr:'بيتزا', emoji:'🍕', isActive:true },
  { id:'4', name:'Mango Lassi',      nameAr:'لاسي بالمانجو',    price:10, category:'Drinks',  categoryAr:'مشروبات', emoji:'🥭', isActive:true },
  { id:'5', name:'French Fries',     nameAr:'بطاطس مقلية',      price:12, category:'Sides',   categoryAr:'إضافات', emoji:'🍟', isActive:true },
];

export default function KioskPage({ vendorId, kioskModeEnabled = false, vendorName, tableNo: initialTable, kioskTheme, brandColor, bgColor, tileColor, fontColor, buttonTextColor, surfaceColor, serviceFeeConfig }) {
  const { t, isAr, itemName, itemDesc } = useLanguage();
  const [menu,         setMenu]         = useState([]);
  const [, setCategories] = useState([]);
  const [cat,          setCat]          = useState('All');
  const [diet,         setDiet]         = useState('none');   // session-only dietary preference
  const [showDietSheet, setShowDietSheet] = useState(true);
  const [showUnverified, setShowUnverified] = useState(false);
  const [search,       setSearch]       = useState('');
  const [loading,      setLoading]      = useState(true);
  const [ordered,      setOrdered]      = useState(false);
  const [orderedCashPending, setOrderedCashPending] = useState(false);
  const [orderNo,      setOrderNo]      = useState('');
  const [idle,         setIdle]         = useState(false);
  const [promoCode,    setPromoCode]    = useState(null);
  const [countdown,    setCountdown]    = useState(5);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [showPayment,  setShowPayment]  = useState(false);
  const [modifierItem, setModifierItem] = useState(null);
  const [bundleItem,   setBundleItem]   = useState(null);
  const [tableNo,      setTableNo]      = useState(initialTable || null);
  const [placing,        setPlacing]        = useState(false);
  const [orderError,     setOrderError]     = useState('');
  const [customerPhone,  setCustomerPhone]  = useState('');

  const { cart, subtotal, tax, total, addItem, setQty, removeItem, clearCart } = useCart();
  const { isOnline } = usePWA();

  const discountAmount = promoCode?.amount || 0;
  const feeConfig      = typeof serviceFeeConfig === 'string' ? (() => { try { return JSON.parse(serviceFeeConfig); } catch(_) { return null; } })() : (serviceFeeConfig || null);
  const serviceFee     = calculateServiceFee(subtotal, feeConfig);
  const finalTotal     = Math.max(0, total - discountAmount + serviceFee.amount);

  useKioskMode({
    enabled: kioskModeEnabled,
    onIdle:  () => setIdle(true),
    onReset: () => { clearCart(); setCat('All'); setSearch(''); setPromoCode(null); setDiet('none'); setShowUnverified(false); setShowDietSheet(true); },
  });

  // Idle timer
  useEffect(() => {
    if (ordered || showPayment || modifierItem) return;
    let timer = setTimeout(() => setIdle(true), IDLE_AFTER);
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => setIdle(true), IDLE_AFTER); };
    const events = ['mousemove','mousedown','touchstart','keydown','scroll'];
    events.forEach(e => document.addEventListener(e, reset, { passive:true }));
    return () => { clearTimeout(timer); events.forEach(e => document.removeEventListener(e, reset)); };
  }, [ordered, showPayment, modifierItem]);

  // Apply customer-facing kiosk theme; reset to dark on unmount (e.g. admin previews kiosk then navigates back)
  useEffect(() => {
    applyTheme(kioskTheme || 'dark', brandColor, bgColor, tileColor, fontColor, buttonTextColor, surfaceColor);
    return () => applyTheme('dark');
  }, [kioskTheme, brandColor, bgColor, tileColor, fontColor, buttonTextColor, surfaceColor]);

  // Load menu
  useEffect(() => {
    getMenu({ vendorId, active: true })
      .then(r => {
        const items = r.data || [];
        setMenu(items);
        const cats = ['All', ...new Set(items.map(i => i.category).filter(Boolean))];
        setCategories(cats);
      })
      .catch(() => {
        setMenu(MOCK_MENU);
        setCategories(['All','Wraps','Burgers','Pizza','Drinks','Sides']);
      })
      .finally(() => setLoading(false));
  }, [vendorId]);

  // Read table from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tbl    = params.get('table');
    if (tbl) setTableNo(tbl);
  }, []);

  // Auto reset after order
  useEffect(() => {
    if (!ordered) return;
    setCountdown(5);
    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(timer);
          setOrdered(false); setCat('All');
          setDiet('none'); setShowUnverified(false); setShowDietSheet(true);
          setSearch(''); setPendingOrder(null); setOrderedCashPending(false);
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [ordered, clearCart]);

  const catLabel = (c) => {
    if (c === 'All') return t(strings.allCategories);
    if (isAr) { const item = menu.find(i => i.category === c); return item?.categoryAr || c; }
    return c;
  };

  const { shown: dietItems, unverified: dietUnverified } = applyDietary(menu.filter(i => i.isActive), diet, showUnverified);
  const visibleCats = categoriesOf(dietItems);
  const activeCat   = visibleCats.includes(cat) ? cat : 'All';

  const filtered = dietItems
    .filter(i => activeCat === 'All' || i.category === activeCat)
    .filter(i => {
      const q = search.toLowerCase();
      return i.name.toLowerCase().includes(q) || (i.nameAr && i.nameAr.includes(search));
    });

  const getQty = (id) => cart.find(i => i.id === id)?.qty || 0;

  const handleItemTap = async (item) => {
    if (item.itemType === 'BUNDLE') { setBundleItem(item); return; }
    try {
      const r = await api.get(`/menu/${item.id}/modifiers`);
      if (r.data?.length > 0) { setModifierItem(item); return; }
    } catch (_) {}
    addItem(item);
  };

  const handleModifierAdd = (configuredItem) => {
    addItem(configuredItem);
    setModifierItem(null);
  };

  const handleBundleAdd = (configuredItem) => {
    addItem(configuredItem);
    setBundleItem(null);
  };

  // ── Create order ONCE then show payment ───────────────────
  const handleProceedToPayment = async () => {
    if (!cart.length || placing) return;
    setOrderError('');

    // Reuse existing pending order if payment failed and customer retries
    if (pendingOrder) { setShowPayment(true); return; }

    setPlacing(true);
    try {
      const res = await api.post('/orders', {
        vendorId:      vendorId,
        items:         cart.map(i => ({ menuItemId: i.id, quantity: i.qty, bundleComponents: i.bundleComponentsDetail || undefined })),
        paymentMode:   'CARD',
        discount:      discountAmount,
        tableNo:       tableNo || undefined,
        sourceKiosk:   'VENDOR_STALL',
        notes:         cart.filter(i => i.modifierNote).map(i => `${itemName(i)}: ${i.modifierNote}`).join(' | '),
        customerPhone: customerPhone.trim() || undefined,
      });
      setPendingOrder(res.data);
      setShowPayment(true);
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Could not place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  // ── Payment success — order number already on pendingOrder ─
  const handlePaymentSuccess = ({ cashPending } = {}) => {
    const num = pendingOrder?.orderNumber || '';
    clearCart();          // clear immediately so switching tabs doesn't leave stale cart
    setPromoCode(null);
    setCustomerPhone('');
    setShowPayment(false);
    setOrderNo(num);
    setOrdered(true);
    setOrderedCashPending(!!cashPending);
  };

  // ── Close payment modal — cancel if unpaid ────────────────
  const handlePaymentClose = async () => {
    setShowPayment(false);
    // Cancel the order if it was created but not paid (payment abandoned)
    if (pendingOrder?.id && !pendingOrder.id.startsWith('demo-')) {
      try {
        await api.patch(`/orders/${pendingOrder.id}/cancel-unpaid`);
      } catch (_) {}
    }
    setPendingOrder(null);
  };

  if (idle) return <IdleScreen vendorName={vendorName} onWake={() => setIdle(false)} />;

  if (ordered) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'var(--bg)', textAlign:'center', padding:40, direction: isAr ? 'rtl' : 'ltr' }}>
      <div style={{ fontSize:80, marginBottom:24 }}>{orderedCashPending ? '🎫' : '✅'}</div>
      <div style={{ fontSize:30, fontWeight:800, color: orderedCashPending ? 'var(--accent)' : 'var(--green)', marginBottom:8 }}>
        {orderedCashPending ? t(strings.payAtCounter) : t(strings.orderPlaced)}
      </div>
      <div style={{ fontSize:22, fontWeight:700, color:'var(--accent)', marginBottom:16 }}>{orderNo}</div>
      {tableNo && <div style={{ fontSize:16, color:'var(--text2)', marginBottom:8 }}>{t(strings.tableNo)} {tableNo}</div>}
      <div style={{ fontSize:16, color:'var(--text2)', marginBottom:8 }}>
        {orderedCashPending ? t(strings.payAtCounterMsg) : t(strings.orderPrepared)}
      </div>
      <div style={{ fontSize:14, color:'var(--text3)', marginTop:24 }}>
        {t(strings.returningIn)} {countdown}{t(strings.seconds)}
      </div>
    </div>
  );

  return (
    <div className="page" style={{ direction: isAr ? 'rtl' : 'ltr' }}>

      {showDietSheet && !loading && (
        <DietarySheet isAr={isAr} initial={diet}
          onContinue={(p) => { setDiet(p || 'none'); setShowDietSheet(false); }}
          onSkip={() => setShowDietSheet(false)} />
      )}

      {!isOnline && (
        <div style={{ background:'#3d2e10', border:'1px solid var(--accent)', borderRadius:8, padding:'8px 16px', marginBottom:16, fontSize:13, color:'var(--accent)' }}>
          📵 {t(strings.offline)}
        </div>
      )}

      {tableNo && (
        <div style={{ background:'#1a2d3d', border:'1px solid var(--blue)', borderRadius:8, padding:'8px 16px', marginBottom:16, fontSize:14, color:'var(--blue)', fontWeight:600 }}>
          🪑 {t(strings.tableNo)} {tableNo}
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, gap:12 }}>
        <div>
          <div className="page-title">{t(strings.whatCraving)}</div>
          <div className="page-sub">{menu.filter(i=>i.isActive).length} {t(strings.itemsAvailable)}</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <LangToggle />
          <input type="text" placeholder={t(strings.searchMenu)} value={search}
            onChange={e => setSearch(e.target.value)} style={{ width:200 }} />
        </div>
      </div>

      {diet !== 'none' && (
        <div style={{ marginBottom:12 }}>
          <DietaryPill preference={diet} isAr={isAr} onClear={() => { setDiet('none'); setShowUnverified(false); }} />
        </div>
      )}

      {/* Category tabs */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
        {visibleCats.map(c => (
          <button key={c} onClick={() => setCat(c)} style={{
            padding:'8px 18px', borderRadius:20, fontSize:13, cursor:'pointer',
            background: activeCat===c ? 'var(--accent)' : 'var(--bg3)',
            color:      activeCat===c ? '#000' : 'var(--text2)',
            border:     `1px solid ${activeCat===c ? 'var(--accent)' : 'var(--border)'}`,
          }}>{catLabel(c)}</button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:20 }}>

        {/* Menu grid */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:16 }}>
          {loading && <div style={{ color:'var(--text3)', gridColumn:'1/-1', textAlign:'center', padding:40 }}>{t(strings.loading)}</div>}
          {filtered.map(item => {
            const qty = getQty(item.id);
            return (
              <div key={item.id}
                onClick={() => !qty && handleItemTap(item)}
                style={{ background:'var(--bg2)', border:`1px solid ${qty>0?'var(--accent)':'var(--border)'}`, borderRadius:'var(--radius)', overflow:'hidden', cursor:'pointer', transition:'border .2s' }}>
                {item.imageUrl
                  ? <img src={item.imageUrl} alt={itemName(item)} style={{ width:'100%', height:110, objectFit:'cover' }} onError={e=>e.target.style.display='none'} />
                  : <div style={{ height:110, display:'flex', alignItems:'center', justifyContent:'center', fontSize:48, background:'var(--bg3)' }}>{item.emoji}</div>
                }
                <div style={{ padding:12, textAlign: isAr ? 'right' : 'left' }}>
                  <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>{itemName(item)}{item.unverified && <UnverifiedTag isAr={isAr} />}</div>
                  <div style={{ fontSize:15, fontWeight:700, color:'var(--accent)' }}>
                    {(item.modifierGroups?.length > 0 || item.itemType === 'BUNDLE') ? `${t(strings.fromPrice)} ` : ''}AED {item.price}
                  </div>
                  {itemDesc(item) && <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>{itemDesc(item)}</div>}
                  {qty > 0 ? (
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, justifyContent: isAr ? 'flex-end' : 'flex-start' }}
                      onClick={e => e.stopPropagation()}>
                      <button onClick={() => setQty(item.id, qty-1)} style={{ width:28, height:28, borderRadius:'50%', background:'var(--bg4)', color:'var(--text)', fontSize:16, border:'1px solid var(--border)', cursor:'pointer' }}>−</button>
                      <span style={{ fontWeight:700, minWidth:20, textAlign:'center' }}>{qty}</span>
                      <button onClick={() => handleItemTap(item)} style={{ width:28, height:28, borderRadius:'50%', background:'var(--accent)', color:'var(--button-text)', fontSize:16, border:'none', cursor:'pointer' }}>+</button>
                    </div>
                  ) : (
                    <button className="btn btn-primary btn-sm" style={{ marginTop:10, width:'100%' }}>
                      {(item.modifierGroups?.length > 0 || item.itemType === 'BUNDLE') ? t(strings.customise) : t(strings.addToCart)}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {!loading && filtered.length === 0 && (
            <div style={{ color:'var(--text3)', gridColumn:'1/-1', textAlign:'center', padding:40 }}>{t(strings.noItems)}</div>
          )}
          {diet !== 'none' && (
            <div style={{ gridColumn:'1/-1' }}>
              <DietaryHiddenNote count={dietUnverified.length} expanded={showUnverified} isAr={isAr} onToggle={() => setShowUnverified(v => !v)} />
            </div>
          )}
        </div>

        {/* Cart */}
        <div className="card" style={{ position:'sticky', top:80, maxHeight:'calc(100vh - 100px)', overflowY:'auto', direction: isAr ? 'rtl' : 'ltr' }}>
          <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>
            {t(strings.yourCart)} <span style={{ color:'var(--text3)', fontWeight:400, fontSize:13 }}>({cart.length} {t(strings.items)})</span>
          </div>

          {cart.length === 0 && (
            <div style={{ color:'var(--text3)', fontSize:13, textAlign:'center', padding:'30px 0' }}>
              {t(strings.addItemsToStart)}
            </div>
          )}

          {cart.map(item => (
            <div key={item.id} style={{ padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:600 }}>{item.emoji} {itemName(item)}</div>
                  {item.bundleComponentsDetail?.length > 0 ? (
                    <div style={{ marginTop:2 }}>
                      {item.bundleComponentsDetail.map((c, ci) => (
                        <div key={ci} style={{ fontSize:11, color:'var(--text2)', paddingInlineStart:10 }}>
                          • {c.componentName}: {c.modifierNote || 'No customization'}
                        </div>
                      ))}
                    </div>
                  ) : item.modifierNote && <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>{item.modifierNote}</div>}
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{item.qty} × AED {item.price.toFixed(2)}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontWeight:700, color:'var(--accent)' }}>AED {(item.price * item.qty).toFixed(2)}</span>
                  <button onClick={() => removeItem(item.id)} style={{ background:'none', border:'none', color:'var(--text3)', fontSize:16, cursor:'pointer' }}>✕</button>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8, justifyContent: isAr ? 'flex-end' : 'flex-start' }}>
                <button onClick={() => setQty(item.id, item.qty-1)} style={{ width:26, height:26, borderRadius:'50%', background:'var(--bg4)', color:'var(--text)', fontSize:14, border:'1px solid var(--border)', cursor:'pointer' }}>−</button>
                <span style={{ fontWeight:700, minWidth:20, textAlign:'center', fontSize:13 }}>{item.qty}</span>
                <button onClick={() => handleItemTap(item)} style={{ width:26, height:26, borderRadius:'50%', background:'var(--accent)', color:'var(--button-text)', fontSize:14, border:'none', cursor:'pointer' }}>+</button>
              </div>
            </div>
          ))}

          {cart.length > 0 && (
            <div>
              <div style={{ marginTop:12 }}>
                <PromoCode subtotal={subtotal} vendorId={vendorId} appliedCode={promoCode} onApply={setPromoCode} onRemove={() => setPromoCode(null)} />
              </div>
              <div style={{ background:'var(--bg3)', borderRadius:'var(--radius-sm)', padding:14, marginTop:12 }}>
                {[
                  [t(strings.subtotal), subtotal.toFixed(2)],
                  [t(strings.vat),      tax.toFixed(2)],
                ].map(([l,v]) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginBottom:6 }}>
                    <span>{l}</span><span>AED {v}</span>
                  </div>
                ))}
                {serviceFee.amount > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginBottom:6 }}>
                    <span>{serviceFee.label}</span><span>AED {serviceFee.amount.toFixed(2)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--green)', marginBottom:6 }}>
                    <span>🎟 {t(strings.discount)}</span><span>- AED {discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:16, marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)' }}>
                  <span>{t(strings.total)}</span><span>AED {finalTotal.toFixed(2)}</span>
                </div>
                <div style={{ marginTop:10 }}>
                  <input type="tel" value={customerPhone}
                    onChange={e => setCustomerPhone(e.target.value)}
                    placeholder="📱 Phone for pickup SMS (optional)"
                    style={{ width:'100%', boxSizing:'border-box' }} />
                </div>
                {orderError && (
                  <div style={{ marginTop:10, padding:'8px 12px', background:'#3d1a1a', color:'#e05a5a', borderRadius:8, fontSize:13, border:'1px solid #5a2a2a' }}>
                    ⚠ {orderError}
                  </div>
                )}
                <button className="btn btn-primary" style={{ width:'100%', marginTop:10, padding:13 }}
                  onClick={handleProceedToPayment}
                  disabled={placing}>
                  {placing ? '⏳ Placing order...' : t(strings.proceedPayment)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modifierItem && (
        <ModifierSelector item={modifierItem} onAdd={handleModifierAdd} onClose={() => setModifierItem(null)} />
      )}

      {bundleItem && (
        <BundleConfigurator item={bundleItem} onAdd={handleBundleAdd} onClose={() => setBundleItem(null)} />
      )}

      {showPayment && pendingOrder && (
        <PaymentModal
          order={pendingOrder}
          vendorId={vendorId}
          sourceKiosk="VENDOR_STALL"
          onSuccess={handlePaymentSuccess}
          onClose={handlePaymentClose}
        />
      )}
    </div>
  );
}
